import { useEffect, useId, useRef } from "react";
import { toggleFilterKey } from "../plantMetadata";

type Option = { key: string; label: string };

type Props = {
  id: string;
  label: string;
  allLabel: string;
  options: Option[];
  selected: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  openId: string | null;
  onOpenIdChange: (id: string | null) => void;
};

function selectionSummary(selected: ReadonlySet<string>, options: Option[], allLabel: string): string {
  if (selected.size === 0) return allLabel;
  if (selected.size === 1) {
    const key = [...selected][0];
    return options.find((o) => o.key === key)?.label ?? allLabel;
  }
  return `${selected.size} selected`;
}

export function MetadataFilterDropdown(props: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const open = props.openId === props.id;
  const summary = selectionSummary(props.selected, props.options, props.allLabel);
  const hasSelection = props.selected.size > 0;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) props.onOpenIdChange(null);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onOpenIdChange(null);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, props.onOpenIdChange]);

  if (props.options.length === 0) return null;

  return (
    <div className="filter-dropdown" ref={rootRef}>
      <span className="filter-dropdown__label muted" id={`${menuId}-label`}>
        {props.label}
      </span>
      <button
        type="button"
        className={`filter-dropdown__trigger${open ? " filter-dropdown__trigger--open" : ""}${
          hasSelection ? " filter-dropdown__trigger--active" : ""
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${menuId}-label`}
        onClick={() => props.onOpenIdChange(open ? null : props.id)}
      >
        <span className="filter-dropdown__value">{summary}</span>
        <span className="filter-dropdown__chevron" aria-hidden>
          ▾
        </span>
      </button>
      {open ? (
        <div className="filter-dropdown__menu" id={menuId} role="listbox" aria-multiselectable="true">
          <div className="filter-dropdown__menu-head">
            <span className="muted">{props.label}</span>
            {hasSelection ? (
              <button
                type="button"
                className="filter-dropdown__menu-action"
                onClick={() => props.onChange(new Set())}
              >
                Clear
              </button>
            ) : (
              <button
                type="button"
                className="filter-dropdown__menu-action"
                onClick={() => props.onChange(new Set(props.options.map((o) => o.key)))}
              >
                Select all
              </button>
            )}
          </div>
          <ul className="filter-dropdown__list">
            {props.options.map((opt) => {
              const checked = props.selected.has(opt.key);
              return (
                <li key={opt.key} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={checked}
                    className={`filter-dropdown__option${checked ? " filter-dropdown__option--checked" : ""}`}
                    onClick={() => props.onChange(toggleFilterKey(props.selected, opt.key))}
                  >
                    <span className="filter-dropdown__check" aria-hidden>
                      {checked ? "✓" : ""}
                    </span>
                    <span>{opt.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
